<script setup lang="ts">
import { computed } from 'vue';
import { PrimaryButton } from '@hfc/ui-core';
import MetalTierContent from '@/page/infrastructure/views/MetalTierContent.vue';
import { useUrlHelper } from '@/page/infrastructure/composition/useUrlHelper';
import { useMetallicPlansStore } from '@/page/infrastructure/composition/useMetallicPlansStore';
import TextSection from '@/page/infrastructure/components/metalPlans/TextSection.vue';
import TextAndImage from '@/page/infrastructure/components/TextAndImage.vue';
import MetalTierCard from '@/page/infrastructure/components/metalPlans/MetalTierCard.vue';
import { usePageStore } from '@/page/infrastructure/composition/usePageStore';
import { MetalPlanData } from '@/page/domain/MetalPlanData';

const { pageState } = usePageStore();

const props = defineProps<{
  planName: string;
}>();

const { getBaseUrl, getCdnUrl } = useUrlHelper();

const { metalPlanPageSections } = useMetallicPlansStore(props.planName);

const metalPlans = computed(() => pageState.value.metalPlans as Array<MetalPlanData>);
const bronze60Plan = computed(() => metalPlans.value.find(plan => plan.key.includes('bronze_60')) as MetalPlanData);
const minimumCoveragePlan = computed(() => metalPlans.value.find(plan => plan.key.includes(props.planName)) as MetalPlanData);
</script>
<template>
  <MetalTierContent v-if="metalPlanPageSections" :metal-plan-page-sections="metalPlanPageSections" :plan-name="planName">
    <template v-slot:afterTextSection="{ pageData }">
      <div v-if="pageData.metalPlansSection" class="w-full bg-blue py-16">
        <div class="flex flex-1 flex-col items-center justify-center max-w-7xl mx-auto relative space-y-6 px-6 lg:px-3">
          <h3 class="text-white font-bold" v-html="pageData.metalPlansSection.title"></h3>
          <div class="text-white text-center flex-col" v-html="pageData.metalPlansSection.subtitle"></div>
          <div class="flex flex-row w-full justify-center space-y-10 lg:space-y-0 lg:space-x-10">
            <MetalTierCard
              :plan="bronze60Plan"
              :show-hsa-badge="true"
              :highlighted="false"
            />
            <MetalTierCard
              :plan="minimumCoveragePlan"
              :show-hsa-badge="true"
              :highlighted="false"
            />
          </div>
          <div class="flex h-full flex-col justify-end">
            <div class="flex w-full justify-center">
              <PrimaryButton v-if="pageData.metalPlansSection.redirectUrl" class="text-xl font-semibold" size="xl" :href="getBaseUrl(pageData.metalPlansSection.redirectUrl)">Compare All Plans</PrimaryButton>
            </div>
          </div>
        </div>
      </div>
      <TextSection v-if="pageData.metalTierHighlight" :page-data="pageData.metalTierHighlight" />
    </template>
    <template v-slot:TwoColumnSection="{ pageData }">
      <div v-if="pageData" class="w-full py-16" :class="pageData.backgroundColor ? pageData.backgroundColor : 'bg-white'">
        <div class="flex flex-col max-w-7xl mx-auto px-6 lg:px-3">
          <div class="flex flex-col w-full items-center">
            <h2 class="font-bold mb-2" v-html="pageData.title"></h2>
            <div v-html="pageData.subtitle"></div>
          </div>
          <TextAndImage div-class="py-2 lg:py-12" :content-direction="pageData.contentDirection">
            <template #text>
              <div class="flex flex-col space-y-4 mt-6 lg:mt-0">
                <p v-html="pageData.content"></p>
              </div>
              <div v-if="pageData.redirectUrl" class="flex mt-10">
                <PrimaryButton :href="pageData.redirectUrl" size="xl" class="text-xl font-semibold">{{ pageData.redirectText }}</PrimaryButton>
              </div>
            </template>
            <template #image>
              <div class="flex flex-1 mt-10 lg:mt-0">
                <img v-if="pageData.imageUrl" class="rounded" :src="getCdnUrl(pageData.imageUrl)" />
              </div>
            </template>
          </TextAndImage>
        </div>
      </div>
    </template>
  </MetalTierContent>
</template>
